import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import type { SharedDay, SharedMeal } from '../../types';
import { Icon, MapPin, Clock, Utensils } from '../../components/ui/Icon';
import { TYPE_ICON, TYPE_COLOR, SharedNote, EmptyNote } from './shared.ui';
import { formatMinutes } from '../../utils/duration';

interface Props {
  days: SharedDay[];
  /** ADR-0008 — meals are SHOWN beside the day. They are not stops and this
   *  page must not make them look like ones: no number, no slot in the route. */
  meals?: SharedMeal[];
}

function DayMeals({ meals }: { meals: SharedMeal[] }) {
  const { t } = useTranslation();
  if (meals.length === 0) return null;
  return (
    <div className="px-4 py-2.5 bg-amber-50/60 dark:bg-amber-900/10 border-t border-amber-100/70 dark:border-amber-900/20">
      <p className="font-mono text-[10px] uppercase tracking-widest text-amber-700/70 dark:text-amber-500/70 mb-1.5">
        {t('shared.sections.meals')}
      </p>
      <div className="space-y-1">
        {meals.map(m => (
          <div key={m.id} className="flex items-start gap-2">
            <Icon as={Utensils} size={12} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-slate-700 dark:text-slate-300">
                <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400 mr-1.5">
                  {t(`meals.slot.${m.slot}`)}
                </span>
                {m.title}
                {m.venue && <span className="text-slate-400"> · {m.venue.name}</span>}
              </p>
              <SharedNote text={m.notes} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SharedItinerary({ days, meals = [] }: Props) {
  const { t } = useTranslation();

  if (days.length === 0) return <EmptyNote>{t('shared.noActivities')}</EmptyNote>;

  return (
    <div className="space-y-4">
      {days.map(day => {
        const dayMeals = meals.filter(m => m.dayId === day.id);
        return (
          <div key={day.id} className="card overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
              <span className="w-6 h-6 rounded-lg bg-teal-500 text-white text-xs font-bold flex items-center justify-center shrink-0">
                {day.dayNumber}
              </span>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                {format(parseISO(day.date), 'EEEE, MMM d')}
                {day.title ? ` · ${day.title}` : ''}
              </p>
            </div>

            {day.activities.length === 0 ? (
              <p className="px-4 py-3 text-xs text-slate-400">{t('shared.noActivities')}</p>
            ) : (
              <div className="divide-y divide-slate-50 dark:divide-slate-800/60">
                {day.activities.map(act => {
                  const ActivityTypeIcon = TYPE_ICON[act.type] ?? MapPin;
                  return (
                    <div key={act.id} className="flex items-start gap-3 px-4 py-3">
                      <div className="w-7 h-7 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon as={ActivityTypeIcon} size={14} className={TYPE_COLOR[act.type] ?? 'text-slate-400'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {act.title}
                          {act.isOptional && (
                            <span className="ml-2 font-mono text-[9px] uppercase tracking-wide text-slate-400 border border-slate-200 dark:border-slate-700 rounded px-1 py-0.5">
                              {t('shared.optional')}
                            </span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap text-2xs text-slate-400">
                          {act.venueName && <span>{act.venueName}</span>}
                          {act.zone && <span>{act.zone}</span>}
                          {act.address && <span className="truncate">{act.address}</span>}
                          {act.durationMins != null && <span>{formatMinutes(act.durationMins)}</span>}
                        </div>
                        <SharedNote text={act.notes} />
                      </div>
                      {act.startTime && (
                        <div className="flex items-center gap-1 text-2xs text-slate-400 shrink-0">
                          <Icon as={Clock} size={12} />
                          {act.startTime}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <DayMeals meals={dayMeals} />
          </div>
        );
      })}
    </div>
  );
}
